<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import StemneConfirmModal from '@/components/ui/StemneConfirmModal.vue'
import type { AgreementKind } from '@/utils/agreementGatePrompt'

// Confirmation step in front of the agreement-gate bounce (#3287). Mounted
// once in the app shell; apiClient.ts opens it through the prompter registry
// when a gated route 403s.
//
// Neutral tone, not destructive: nothing is being destroyed, and rust on a
// legal-consent prompt would read as an alarm the situation doesn't warrant.
const props = defineProps<{
  show: boolean
  kind: AgreementKind
}>()

const emit = defineEmits<{
  done: [ok: boolean]
}>()

const { t } = useI18n()

// The confirm button LEAVES for the accept screen — it does not accept
// anything. The label says so ("Se avtalen og fortsett", matching
// DpaRequiredView's CTA) rather than the "Godta og fortsett" of the accept
// screen itself: a button that reads as granting consent, on a surface where
// the agreement text isn't visible, is not consent.
const title = computed(() =>
  props.kind === 'terms' ? t('agreementGateModal.termsTitle') : t('agreementGateModal.dpaTitle'),
)
const body = computed(() =>
  props.kind === 'terms' ? t('agreementGateModal.termsBody') : t('agreementGateModal.dpaBody'),
)
</script>

<template>
  <StemneConfirmModal
    :show="show"
    :title="title"
    :body="body"
    variant="neutral"
    :confirm-label="t('agreementGateModal.confirm')"
    @confirm="emit('done', true)"
    @cancel="emit('done', false)"
  />
</template>
